import { OtherTheme, Theme } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useTheme } from '~/react-query-hooks/themes/useThemeOverview';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { ThemeLogoIcon } from '~/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { RatingPill } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/rating-pill/RatingPill';
import { SubTabsType } from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import * as S from './ThematicFocusCard.styles';
import { isTheme, useThemeTypeColors } from './utils';

export interface ThematicFocusCardProps {
  theme: Theme | OtherTheme;
  isLast?: boolean;
  withPill?: boolean;
}

export const ThematicFocusCard = ({
  theme,
  isLast = false,
  withPill = false,
}: ThematicFocusCardProps) => {
  const { t } = useTranslation(['earnings']);
  const { params } = useQueryParams();
  const navigate = useNavigate();
  const { trackThemeOpen } = usePosthogEvents();
  const themeTypeColors = useThemeTypeColors();

  const isExistingTheme = isTheme(theme);
  const themeId = isExistingTheme ? theme.theme_id : '';
  const { isFetching, isError, error, data } = useTheme({ themeId });
  const themeName = isExistingTheme ? data?.name ?? '' : theme.name;
  const themeType = isExistingTheme
    ? t('earnings:earningsInsight.drawer.investmentTheme')
    : t(`earnings:earningsInsight.drawer.themeType.${theme.type}`);
  const keyThemeType = isExistingTheme ? 'Investment' : theme.type;
  const themeColor = themeTypeColors[keyThemeType] ?? 'var(--text-default)';

  if (isError || (isExistingTheme && !data && !isFetching)) {
    throw new Error(error?.message);
  }

  const handleClick = () => {
    if (isExistingTheme) {
      const earningsInsightType =
        params[queryKeys.activeDrawerTab] === SubTabsType.Preview
          ? 'preview'
          : 'recap';
      trackThemeOpen(
        POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER,
        earningsInsightType
      );
      navigate(`${appPaths.themeOverview}/${themeId}`);
    }
  };

  const getContent = () => {
    if (isFetching) {
      return <S.StyledSkeletonLoader />;
    } else {
      return (
        <>
          <S.HeaderWrapper>
            <S.HeaderLeftWrapper>
              {isExistingTheme && (
                <ThemeLogoIcon
                  name={themeName}
                  logo_url={data?.logo_url}
                  size={40}
                />
              )}
              <S.TypeAndTitleWrapper>
                <S.TypeLabel $themeTypeColors={themeColor}>
                  {themeType}
                </S.TypeLabel>
                <S.TitleLabel>{themeName}</S.TitleLabel>
              </S.TypeAndTitleWrapper>
            </S.HeaderLeftWrapper>
            {withPill && <RatingPill rating={theme.rating} />}
          </S.HeaderWrapper>
          <S.RelationLabel>{theme.relationship}</S.RelationLabel>
        </>
      );
    }
  };

  return (
    <S.Root
      $isLast={isLast}
      $isExistingTheme={isExistingTheme}
      onClick={handleClick}
    >
      {getContent()}
    </S.Root>
  );
};
